import type { PlanCell } from '../types/plan';
import { planCellKey } from './planCells';

/**
 * Entity types in the plan
 */
export type EntityType = 'bed' | 'plot' | 'path' | 'obstacle';

/**
 * Bed entity - group of cells with same bedId
 */
export interface BedEntity {
  type: 'bed';
  bedId: string; // CN-0012: UUID-only
  cells: Array<{ row: number; col: number }>;
}

/**
 * Path entity - individual or grouped path cells
 */
export interface PathEntity {
  type: 'path';
  cells: Array<{ row: number; col: number }>;
}

/**
 * Obstacle entity - individual or grouped obstacle cells
 */
export interface ObstacleEntity {
  type: 'obstacle';
  cells: Array<{ row: number; col: number }>;
}

/**
 * Plot entity - group of cells with same plotId (field-level planning)
 */
export interface PlotEntity {
  type: 'plot';
  plotId: string; // CN-0012: UUID-only
  cells: Array<{ row: number; col: number }>;
}

/**
 * Union type for all plan entities
 */
export type PlanEntity = BedEntity | PlotEntity | PathEntity | ObstacleEntity;

/**
 * Lookup map: "row:col" → entity
 * Multiple cells can point to the same entity reference (for beds)
 */
export type EntityLookup = Map<string, PlanEntity>;

/**
 * Build entity lookup from plan cells
 * Groups bed cells by bedId, treats path/obstacle as individual entities
 */
export const buildEntityLookup = (cells: PlanCell[]): EntityLookup => {
  const lookup = new Map<string, PlanEntity>();
  const bedCells = new Map<string, Array<{ row: number; col: number }>>(); // CN-0012: UUID-only
  const plotCells = new Map<string, Array<{ row: number; col: number }>>(); // CN-0012: UUID-only
  
  cells.forEach((cell) => {
    const key = planCellKey(cell.row, cell.col);
    
    if (cell.kind === 'bed' && cell.bedId != null) {
      // Group cells by bedId
      if (!bedCells.has(cell.bedId)) {
        bedCells.set(cell.bedId, []);
      }
      bedCells.get(cell.bedId)!.push({ row: cell.row, col: cell.col });
    } else if (cell.kind === 'plot' && cell.plotId != null) {
      // Group cells by plotId
      if (!plotCells.has(cell.plotId)) {
        plotCells.set(cell.plotId, []);
      }
      plotCells.get(cell.plotId)!.push({ row: cell.row, col: cell.col });
    } else if (cell.kind === 'path') {
      // Each path cell is independent (can be merged later)
      const pathEntity: PathEntity = {
        type: 'path',
        cells: [{ row: cell.row, col: cell.col }],
      };
      lookup.set(key, pathEntity);
    } else if (cell.kind === 'obstacle') {
      const obstacleEntity: ObstacleEntity = {
        type: 'obstacle',
        cells: [{ row: cell.row, col: cell.col }],
      };
      lookup.set(key, obstacleEntity);
    }
  });
  
  // Create bed entities and map all cells to the same entity reference
  bedCells.forEach((cells, bedId) => {
    const bedEntity: BedEntity = { type: 'bed', bedId, cells };
    cells.forEach(({ row, col }) => {
      lookup.set(planCellKey(row, col), bedEntity);
    });
  });
  
  // Create plot entities and map all cells to the same entity reference
  plotCells.forEach((cells, plotId) => {
    const plotEntity: PlotEntity = { type: 'plot', plotId, cells };
    cells.forEach(({ row, col }) => {
      lookup.set(planCellKey(row, col), plotEntity);
    });
  });
  
  return lookup;
};

/**
 * Find entity by cell coordinates
 */
export const findEntityByCell = (
  row: number,
  col: number,
  lookup: EntityLookup
): PlanEntity | null => {
  return lookup.get(planCellKey(row, col)) ?? null;
};

/**
 * Get all cell keys belonging to an entity
 */
export const getEntityCellKeys = (entity: PlanEntity): Set<string> => {
  const keys = new Set<string>();
  entity.cells.forEach(({ row, col }) => {
    keys.add(planCellKey(row, col));
  });
  return keys;
};

/**
 * Check if entity is a bed
 */
export const isBedEntity = (entity: PlanEntity): entity is BedEntity => {
  return entity.type === 'bed';
};

/**
 * Check if entity is a plot
 */
export const isPlotEntity = (entity: PlanEntity): entity is PlotEntity => {
  return entity.type === 'plot';
};

/**
 * Get entity description for UI
 */
export const getEntityDescription = (entity: PlanEntity): string => {
  if (entity.type === 'bed') {
    return `Грядка #${entity.bedId}, ${entity.cells.length} ${
      entity.cells.length === 1 ? 'клетка' : 'клеток'
    }`;
  }
  if (entity.type === 'plot') {
    return `Участок #${entity.plotId}, ${entity.cells.length} ${
      entity.cells.length === 1 ? 'клетка' : 'клеток'
    }`;
  }
  if (entity.type === 'path') {
    return `Дорожка, ${entity.cells.length} ${
      entity.cells.length === 1 ? 'клетка' : 'клеток'
    }`;
  }
  return `Препятствие, ${entity.cells.length} ${
    entity.cells.length === 1 ? 'клетка' : 'клеток'
  }`;
};
